import { CORE_API_BASE } from "@/config/apiConfig";
import { apiFetch } from "@/services/apiService";
import { languageUtils } from "@/utils/languageUtils";
import type { RideApiSeat, RideApiStop } from "@/types/rideDetails";

/** A single traveller entry sent in the booking payload. */
export interface BookingTraveller {
  sid: number;
  sn: string;
  nm: string;
  gen: string;
  Tnotes: string;
  pup: { stopid: number };
  dop: { stopid: number };
}

export interface BookRideParams {
  /** Numeric ride id (rideOffer.rid). */
  rid: number;
  travellers: BookingTraveller[];
  /** Booking purpose; defaults to "Personal". */
  purpose?: string;
}

export interface BookRideResult {
  message: string;
  bookingId: number;
  bookingNumber: string;
}

export interface CreateTransactionParams {
  rid: number;
  bookingId: number;
  /** Total price for the booking. */
  totalPrice: number;
  /** Number of seats selected. */
  seatCount: number;
}

const DEFAULT_TRAVELLER_NOTES = "Prefers window seat";

/** First seat uses the booker's name; additional seats are Guest 1, Guest 2, … */
export function getTravellerName(seatIndex: number, bookerName: string): string {
  if (seatIndex === 0) {
    return bookerName.trim() || "Guest";
  }
  return `Guest ${seatIndex}`;
}

/** Maps a stored gender label onto the API's expected enum. */
export function toApiGender(gender: string): string {
  const normalized = (gender || "").trim().toLowerCase();
  if (normalized === "male") return "MALE";
  if (normalized === "female") return "FEMALE";
  return "OTHER";
}

/** Returns the AVAILABLE seats, capped at the number of seats requested. */
export function pickAvailableSeats(
  seating: RideApiSeat[],
  seatCount: number
): RideApiSeat[] {
  return seating
    .filter((seat) => seat.ss === "AVAILABLE" && seat.seatid != null)
    .slice(0, seatCount);
}

/**
 * Builds the traveller array for a booking. Every selected available seat is assigned to
 * the logged-in guest, picked up/dropped off at the chosen stops.
 */
export function buildTravellers({
  seating,
  seatCount,
  guestName,
  gender,
  pickupStopId,
  dropStopId,
}: {
  seating: RideApiSeat[];
  seatCount: number;
  guestName: string;
  gender: string;
  pickupStopId: number;
  dropStopId: number;
}): BookingTraveller[] {
  const seats = pickAvailableSeats(seating, seatCount);
  const apiGender = toApiGender(gender);

  return seats.map((seat, index) => ({
    sid: seat.seatid as number,
    sn: seat.sn ?? "",
    nm: getTravellerName(index, guestName),
    gen: apiGender,
    Tnotes: DEFAULT_TRAVELLER_NOTES,
    pup: { stopid: pickupStopId },
    dop: { stopid: dropStopId },
  }));
}

/** Books seats on a ride for the authenticated guest. */
export async function bookRide({
  rid,
  travellers,
  purpose = "Personal",
}: BookRideParams): Promise<BookRideResult> {
  const lang = languageUtils.getCurrentLanguage();
  const url = `${CORE_API_BASE}/publish_or_book_ride?context=findride&type=booknow`;
  const requestBody = {
    rdo: { rid },
    rdb: { purpose, trv: travellers },
  };

  console.log("[BookRide] Draft call → POST", url);
  console.log("[BookRide] Request body:", requestBody);

  const res = await apiFetch(url, {
    method: "POST",
    headers: {
      accept: "*/*",
      lang,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => "");
    console.error("[BookRide] Response error:", res.status, errorText);
    throw new Error(`Unable to book the ride (status ${res.status}).`);
  }

  const data = await res.json();
  console.log("[BookRide] Response:", data);
  const bookingId = data?.BookingId ?? data?.bookingId;
  const bookingNumber = data?.BookingNumber ?? data?.bookingNumber;

  if (bookingId == null) {
    throw new Error("Booking response did not include a booking id.");
  }

  return {
    message: data?.Message ?? data?.message ?? "Booking saved.",
    bookingId,
    bookingNumber: String(bookingNumber ?? ""),
  };
}

/** Creates the booking payment transaction (cash by default). */
export async function createBookingTransaction({
  rid,
  bookingId,
  totalPrice,
  seatCount,
}: CreateTransactionParams): Promise<void> {
  const lang = languageUtils.getCurrentLanguage();
  const url = `${CORE_API_BASE}/api/RideTransaction/CreateTransaction?TransactionType=booking`;
  const requestBody = {
    rid,
    pf: 0,
    pt: "CASH",
    bid: bookingId,
    rp: totalPrice,
    nos: seatCount,
  };

  console.log("[CreateTransaction] → POST", url);
  console.log("[CreateTransaction] Request body:", requestBody);

  const res = await apiFetch(url, {
    method: "POST",
    headers: {
      accept: "*/*",
      lang,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => "");
    console.error("[CreateTransaction] Response error:", res.status, errorText);
    throw new Error(`Unable to create the booking transaction (status ${res.status}).`);
  }

  const data = await res.text().catch(() => "");
  console.log("[CreateTransaction] Response:", data);
}

export { getPickupStops as getRoutePickupStops } from "@/utils/rideRouteStops";

/** Stop types offered as pickup choices during booking. */
const PICKUP_STOP_TYPES = new Set(["START", "PICKUP", "ENROUTE"]);

export function getPickupStops(
  stops: RideApiStop[],
  /** Ride start point (`route.sp`) — used when no START/PICKUP/ENROUTE stops exist. */
  startPoint?: RideApiStop
): RideApiStop[] {
  const pickupStops = stops.filter(
    (s) =>
      s.stoptype != null && PICKUP_STOP_TYPES.has(s.stoptype.toUpperCase())
  );

  if (pickupStops.length > 0) return pickupStops;

  if (startPoint?.stopid != null) {
    return [startPoint];
  }

  return [];
}

/** Stop types offered as drop-off choices during booking (excludes END). */
const DROP_STOP_TYPES = new Set(["ENROUTE", "DROP"]);

export function getDropStops(
  stops: RideApiStop[],
  selectedPickupStopId?: number,
  /** Ride end point (`route.ep`) — used when no DROP/ENROUTE stops exist. */
  endPoint?: RideApiStop
): RideApiStop[] {
  const dropStops = stops.filter(
    (s) =>
      s.stoptype != null &&
      DROP_STOP_TYPES.has(s.stoptype.toUpperCase()) &&
      s.stopid !== selectedPickupStopId
  );

  if (dropStops.length > 0) return dropStops;

  if (
    endPoint?.stopid != null &&
    endPoint.stopid !== selectedPickupStopId
  ) {
    return [endPoint];
  }

  return [];
}
